import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { isDefaultGranularity } from './emissionGranularity';

dayjs.extend(utc);

/**
 * The 2026 default generation was BACKDATED to 2023-01-01 by a backend migration
 * (backend `src/population/migrations/migrateEmissionFactorsAndUpdateShipments.ts:441-447`)
 * so that ONE row could also answer 2023-2025 reports through the resolver's
 * `date <= reportingPeriod.to` filter. The source rows carried a 2026 start.
 *
 * So on a default row `date` means "earliest reporting period this row may be
 * selected for", NOT "valid from" — and printing it gives a 2026 default a
 * 1 Jan 2023 start, which is the bug this module exists to hide.
 *
 * The backdated generation is identifiable from the row alone, which is why no
 * caller has to know which reporting year is on screen. Across all 51,270
 * default keys in the seed fixture, a Global/Country row whose `lastRecordDate`
 * falls in 2026 is ALWAYS that generation (24,921 keys) and NEVER one of the
 * 26,349 genuine 2023-start rows — the EU-ETS zero factors and the fertiliser
 * family, which all end 2040-12-31 and whose 2023 date is real. The 2027 and
 * 2028 generations carry truthful dates and are left alone. Counts, the
 * exactness check and the decision record:
 * `docs/features/default-values-2026-from-date/00-OVERVIEW.md`.
 *
 * WORKAROUND, NOT A FIX. The durable repair is backend-side (OI-1): un-backdate
 * those rows and widen the resolver to
 * `date <= period.to AND lastRecordDate >= period.from`. Until that lands every
 * new consumer of a default factor's `date` has to route through here — the
 * same trap `emissionGranularity` exists to stop.
 */
const BACKDATED_END_YEAR = 2026;
const BACKDATED_TRUE_START = '2026-01-01';

/**
 * The date a default emission factor is genuinely valid from.
 *
 * @param {object|null|undefined} factor - An emission factor as the API returns
 *   it (`date`, `lastRecordDate`, `granularity`).
 * @returns {string|Date|null} The corrected start for the backdated generation,
 *   the factor's own `date` for everything else, or `null` when it has none.
 */
export const defaultFactorValidFrom = (factor) => {
  const date = factor?.date ?? null;
  if (!date) return null;

  // Actual factors — and Annex IV's `Unknown` rows, which start 2026-01-01
  // already — carry honest dates.
  if (!isDefaultGranularity(factor?.granularity)) return date;

  // UTC throughout: `lastRecordDate` is a date-only boundary at UTC midnight, so
  // a local-time read lands in the previous year anywhere west of UTC.
  const end = factor?.lastRecordDate ? dayjs.utc(factor.lastRecordDate) : null;
  if (end?.isValid() && end.year() === BACKDATED_END_YEAR) return BACKDATED_TRUE_START;

  return date;
};

export default defaultFactorValidFrom;
